// TelcoTrack — mustr textu „Předání povrchů“ (Documents / protokol).
// Dříve client-only ve stavebním deníku; single source of truth pro web + backend.

import { ROLES } from '../constants/roles.constants';

export interface PredaniPovrchuNoteInput {
  obec: string;
  /** YYYY-MM-DD */
  effectiveDate: string;
  /** YYYY-MM-DD */
  protocolDate: string;
  defectsFound: boolean;
  defectsDescription?: string;
  defectsRemediationDeadline?: string;
  /** Free-text: which streets / from where to where is being handed over. */
  predanyUsek?: string;
}

/**
 * Role → display label on the předávající (ProTelco) side of Předání povrchů.
 * Distinct from generic ROLE_LABELS_CS (UI role chips).
 */
export const SURFACE_HANDOVER_ROLE_LABELS = {
  [ROLES.STAVBA]: 'Vedoucí stavby',
  [ROLES.MONTAZ]: 'Technik optických sítí',
  [ROLES.OPTIKA]: 'Technik optických sítí',
  [ROLES.ZAFUK]: 'Technik optických sítí',
  [ROLES.EDITOR]: 'Technik optických sítí',
  [ROLES.ADMIN]: 'Koordinátor stavby',
  [ROLES.SUPERADMIN]: 'Koordinátor stavby',
} as const;

/** Roles allowed to create Předání povrchů (not the same as document upload). */
export const SURFACE_HANDOVER_CREATE_ROLES: readonly string[] = [
  ROLES.SUPERADMIN,
  ROLES.ADMIN,
  ROLES.STAVBA,
  ROLES.MONTAZ,
  ROLES.OPTIKA,
  ROLES.ZAFUK,
  ROLES.EDITOR,
] as const;

/**
 * Priority when the user has multiple matching roles:
 * admin/superadmin > stavba > montaz/optika/zafuk/editor.
 */
const SURFACE_HANDOVER_ROLE_PRIORITY: readonly string[] = [
  ROLES.SUPERADMIN,
  ROLES.ADMIN,
  ROLES.STAVBA,
  ROLES.MONTAZ,
  ROLES.OPTIKA,
  ROLES.ZAFUK,
  ROLES.EDITOR,
] as const;

function normalizeRoleList(roles: string[] | undefined | null): string[] {
  if (!roles?.length) return [];
  return roles.map((r) => (r || '').toLowerCase().trim()).filter(Boolean);
}

/** Dokumenty „+“ → Předání povrchů (upload stays on canCreatePolygonDocuments). */
export function canCreateSurfaceHandover(roles: string[] | undefined | null): boolean {
  const allowed = new Set(SURFACE_HANDOVER_CREATE_ROLES);
  return normalizeRoleList(roles).some((r) => allowed.has(r));
}

/**
 * Prefill „Role“ for předávající from the logged-in user's roles.
 * Returns empty string if none of the allowed roles match.
 */
export function resolveSurfaceHandoverRoleLabel(roles: string[] | undefined | null): string {
  const normalized = new Set(normalizeRoleList(roles));
  for (const role of SURFACE_HANDOVER_ROLE_PRIORITY) {
    if (!normalized.has(role)) continue;
    const label =
      SURFACE_HANDOVER_ROLE_LABELS[role as keyof typeof SURFACE_HANDOVER_ROLE_LABELS];
    if (label) return label;
  }
  return '';
}

/**
 * Display name for forms: first+last → displayName → email.
 * Matches common TelcoTrack user-list patterns.
 */
export function formatUserDisplayName(input: {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
}): string {
  const fromParts = `${(input.firstName ?? '').trim()} ${(input.lastName ?? '').trim()}`.trim();
  if (fromParts) return fromParts;
  const display = (input.displayName ?? '').trim();
  if (display) return display;
  return (input.email ?? '').trim();
}

/** „Jméno“ nebo „Jméno, role“ pro blok V zastoupení / podpis. */
export function formatPredaniZastupceLabel(name: string, role?: string): string {
  const n = name.trim();
  const r = (role ?? '').trim();
  if (!n) return r;
  if (!r) return n;
  return `${n}, ${r}`;
}

/** Přebírající: jméno + příjmení (+ role) → text „V zastoupení“. */
export function formatPredaniReceiverZastupce(
  firstName: string,
  lastName: string,
  role?: string,
): string {
  const name = `${firstName.trim()} ${lastName.trim()}`.trim();
  return formatPredaniZastupceLabel(name, role);
}

/** Formát data pro právní text (dd.mm.yyyy). */
export function formatPredaniLegalDate(isoDate: string): string {
  const trimmed = isoDate.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const parsed = new Date(trimmed);
  if (!Number.isFinite(parsed.getTime())) return trimmed || '—';
  return new Intl.DateTimeFormat('cs-CZ', {
    timeZone: 'Europe/Prague',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

/** Řádek „Předaný úsek: …“ pro mustr / PDF; `null` když je pole prázdné (neemitovat nadpis). */
export function formatPredanyUsekLine(predanyUsek?: string | null): string | null {
  const trimmed = (predanyUsek ?? '').trim();
  return trimmed ? `Předaný úsek: ${trimmed}` : null;
}

/** Sestaví editovatelný text těla protokolu „Předání povrchů“. */
export function buildPredaniPovrchuNote(input: PredaniPovrchuNoteInput): string {
  const obec = input.obec.trim() || '…';
  const effective = formatPredaniLegalDate(input.effectiveDate);
  const protocol = formatPredaniLegalDate(input.protocolDate);
  const predanyUsekLine = formatPredanyUsekLine(input.predanyUsek);

  const lines: string[] = [
    `Předávající předává a přebírající přebírá povrchy v rámci výše specifikované akce. Obec ${obec} přebírá úpravu povrchů s platností od ${effective}.`,
  ];
  if (predanyUsekLine) {
    lines.push(predanyUsekLine);
  }
  lines.push(
    'Na provedený zásyp a povrch se vztahuje záruční lhůta v délce 36 měsíců.',
    '',
  );

  if (input.defectsFound) {
    const desc = (input.defectsDescription ?? '').trim() || '…';
    const deadline = (input.defectsRemediationDeadline ?? '').trim() || '…';
    lines.push(
      `Při předání a prohlídce zástupcem obce byly zjištěny následující závady a nedodělky: ${desc}`,
      '',
      `Termín pro odstranění: Zjištěné závady a nedodělky odstraní předávající organizace do: ${deadline}`,
    );
  } else {
    lines.push('Dílo bylo předáno a převzato bez zjevných vad a nedodělků.');
  }

  lines.push(
    '',
    'Protokol byl přečten, schválen a na znak souhlasu podepsán zástupci obou stran.',
    `V ${obec} dne: ${protocol}`,
  );

  return lines.join('\n');
}
